import type { ReactNode } from 'react';
import { cn } from '../lib/utils';
import type { SuiteAppConfig } from './tokens';

/** A single statistic chip shown on a hub app-card. */
export interface HubStat {
  label: string;
  value: string | number;
}

/** A hub app-card describing one app plus quick stats. */
export interface HubAppCard {
  app: SuiteAppConfig;
  /** Quick-glance stats, supplied by the host app. */
  stats?: HubStat[];
  /** Invoked when the card is opened. */
  onOpen?: () => void;
}

function AppIconTile({ label, accent }: { label: string; accent: string }) {
  return (
    <div
      className="flex h-10 w-10 items-center justify-center rounded-[10px] font-extrabold text-white"
      style={{ background: accent }}
    >
      <span className={label.length > 1 ? 'text-[13px] tracking-wide' : 'text-base'}>{label}</span>
    </div>
  );
}

function HubCard({ card }: { card: HubAppCard }) {
  const { app, stats, onOpen } = card;
  return (
    <button
      type="button"
      onClick={onOpen}
      disabled={!onOpen}
      className="flex flex-col gap-3 rounded-[14px] border border-slate-200 bg-white p-5 text-left transition-[box-shadow,transform] hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-8px_rgb(0_0_0/0.15)] disabled:cursor-default disabled:hover:translate-y-0 disabled:hover:shadow-none"
    >
      <AppIconTile label={app.iconLabel} accent={app.accent} />
      <h3 className="text-[15.5px] font-bold text-slate-800">{app.name}</h3>
      {app.description && (
        <p className="flex-1 text-[12.5px] leading-relaxed text-slate-500">{app.description}</p>
      )}
      {stats && stats.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {stats.map((s) => (
            <span
              key={s.label}
              className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1 text-[11px] text-slate-600"
            >
              <b className="text-slate-800">{s.value}</b> {s.label}
            </span>
          ))}
        </div>
      )}
      <span
        className="flex items-center gap-1 text-[12.5px] font-semibold"
        style={{ color: app.accent }}
      >
        Open →
      </span>
    </button>
  );
}

export interface SuiteHubProps {
  /** Greeting/title, e.g. "Good morning, Amalia". */
  title?: string;
  /** Subtitle under the greeting. */
  subtitle?: string;
  /** App cards to display in the grid. */
  cards: HubAppCard[];
  /**
   * Recent-activity content. Empty in PR1 — real feed (from the notifications
   * API) is wired in PR4. When omitted, an empty-state placeholder shows.
   */
  activity?: ReactNode;
  /**
   * Shown in place of the card grid when `cards` is empty — which means the
   * user holds no app grants at all, not that the suite has no apps. Defaults
   * to a generic "no applications yet" panel.
   */
  emptyState?: ReactNode;
  className?: string;
}

/**
 * Suite home / hub screen: greeting, a grid of app cards, and a recent-activity
 * panel. PR1 ships the layout with static cards and an empty activity state.
 */
export function SuiteHub({
  title = 'Welcome',
  subtitle = 'Everything across the MAP suite, in one place.',
  cards,
  activity,
  emptyState,
  className,
}: SuiteHubProps) {
  return (
    <div className={cn('max-w-[1200px] px-8 py-7', className)}>
      <div>
        <h1 className="text-[22px] font-bold tracking-[-0.3px] text-slate-800">{title}</h1>
        <p className="mt-1 text-[13.5px] text-slate-500">{subtitle}</p>
      </div>

      {cards.length > 0 ? (
        <div className="mt-[22px] grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cards.map((card) => (
            <HubCard key={card.app.id} card={card} />
          ))}
        </div>
      ) : (
        // A bare grid would render as a blank gap, which reads as a page that
        // failed to load rather than an account still waiting on access.
        (emptyState ?? (
          <div className="mt-[22px] rounded-[14px] border border-dashed border-slate-200 bg-white px-6 py-12 text-center">
            <h2 className="text-[15.5px] font-bold text-slate-800">No applications yet</h2>
            <p className="mx-auto mt-2 max-w-sm text-[12.5px] leading-relaxed text-slate-500">
              You haven&rsquo;t been granted access to any apps in the MAP suite. Contact an
              administrator to request access.
            </p>
          </div>
        ))
      )}

      <div className="mt-[22px] rounded-[14px] border border-slate-200 bg-white">
        <h2 className="px-5 pb-1.5 pt-3.5 text-[13px] font-semibold uppercase tracking-wide text-slate-500">
          Recent across the suite
        </h2>
        {activity ?? (
          <div className="px-5 py-10 text-center text-[13px] text-slate-400">
            No recent activity yet.
          </div>
        )}
      </div>
    </div>
  );
}

export type { SuiteAppConfig };
